import type { MapViewState } from './yardMapUtils'

/**
 * Pure touch-gesture decision logic for the switch-list preview camera.
 * The Konva handlers in SwitchListPreviewMapModal own the event stream;
 * everything testable — dead-zone, pause-follow, tap suppression, pan/zoom
 * math — lives here so it runs under vitest without a stage or a DOM.
 */

export interface Point {
  x: number
  y: number
}

export interface RectLike {
  left: number
  top: number
}

export interface TouchLike {
  clientX: number
  clientY: number
}

export type GestureMode = 'none' | 'pan' | 'pinch'

export interface GestureState {
  mode: GestureMode
  /** Pan anchor of the *current* frame (re-anchored on mode transitions). */
  lastX: number
  lastY: number
  /** Finger-down origin — pan deltas are measured from here until the
   *  dead zone is crossed, so activation applies the full accumulated
   *  motion instead of dropping the first few px. */
  startX: number
  startY: number
  /** Last two-finger distance — pinch zoom is ratio-driven off this. */
  pinchDist: number
}

/** Finger travel below this still counts as a tap — the camera must not
 *  move and Follow must not pause on tap jitter. */
export const TOUCH_TAP_DEAD_ZONE_PX = 6
/** Post-gesture window that swallows the synthesized tap a pan/pinch
 *  release would otherwise fire. */
export const TAP_SUPPRESS_MS = 350
/** Pinch ratios inside ±0.5% are treated as no-zoom — sub-pixel finger
 *  jitter would otherwise creep the scale every frame. */
export const PINCH_RATIO_EPSILON = 0.005

export const IDLE_GESTURE: GestureState = {
  mode: 'none',
  lastX: 0,
  lastY: 0,
  startX: 0,
  startY: 0,
  pinchDist: 0,
}

export const touchMidpoint = (t0: TouchLike, t1: TouchLike, rect: RectLike): Point => ({
  x: (t0.clientX + t1.clientX) / 2 - rect.left,
  y: (t0.clientY + t1.clientY) / 2 - rect.top,
})

export const touchDistance = (t0: TouchLike, t1: TouchLike): number =>
  Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY)

const toStagePoint = (t: TouchLike, rect: RectLike): Point => ({
  x: t.clientX - rect.left,
  y: t.clientY - rect.top,
})

/**
 * touchstart → new gesture. Two fingers always start as pinch (anchored,
 * deltas applied from the next move frame so the second finger landing
 * doesn't jump the camera); one finger starts as an un-activated pan.
 */
export const gestureStart = (touches: TouchLike[], rect: RectLike): GestureState => {
  if (touches.length >= 2) {
    const mid = touchMidpoint(touches[0], touches[1], rect)
    return { mode: 'pinch', lastX: mid.x, lastY: mid.y, startX: mid.x, startY: mid.y, pinchDist: touchDistance(touches[0], touches[1]) }
  }
  if (touches.length === 1) {
    const p = toStagePoint(touches[0], rect)
    return { mode: 'pan', lastX: p.x, lastY: p.y, startX: p.x, startY: p.y, pinchDist: 0 }
  }
  return { ...IDLE_GESTURE }
}

export interface PanMove {
  kind: 'pan'
  /** Stage-space delta to apply to the camera this frame. */
  dx: number
  dy: number
  /** True on the frame the dead zone was crossed — the accumulated
   *  start→current delta is delivered as one dx/dy so no travel is lost. */
  activated: boolean
}

export interface PinchMove {
  kind: 'pinch'
  /** Midpoint drift — pans the camera by the same math as one finger. */
  dx: number
  dy: number
  midX: number
  midY: number
  /** dist / lastDist — pass through zoomAtPoint when outside the epsilon. */
  ratio: number
}

export interface GestureMoveResult {
  gesture: GestureState
  move: PanMove | PinchMove | null
}

/**
 * touchmove → { next gesture, camera effect }. `move` is null when the
 * frame carries no camera change (dead-zone pan jitter, pinch anchor
 * frames, epsilon-ratio pinch frames still report a move — the caller
 * decides application).
 */
export const gestureMove = (g: GestureState, touches: TouchLike[], rect: RectLike): GestureMoveResult => {
  if (touches.length >= 2) {
    const mid = touchMidpoint(touches[0], touches[1], rect)
    const dist = touchDistance(touches[0], touches[1])
    // First two-finger frame (pan→pinch transition): anchor only. Applying
    // mid/dist deltas here would jump the camera by the inter-finger
    // geometry — the classic "pinch that starts as one finger" jolt.
    if (g.mode !== 'pinch' || g.pinchDist <= 0) {
      return {
        gesture: { mode: 'pinch', lastX: mid.x, lastY: mid.y, startX: mid.x, startY: mid.y, pinchDist: dist },
        move: null,
      }
    }
    const ratio = dist / g.pinchDist
    return {
      gesture: { ...g, lastX: mid.x, lastY: mid.y, pinchDist: dist },
      move: {
        kind: 'pinch',
        dx: mid.x - g.lastX,
        dy: mid.y - g.lastY,
        midX: mid.x,
        midY: mid.y,
        ratio,
      },
    }
  }
  if (touches.length === 1 && g.mode === 'pan') {
    const p = toStagePoint(touches[0], rect)
    const activated = !isPanActive(g) && isPastDeadZone(g, p.x, p.y)
    // Still inside the dead zone: track the anchor frame but emit no
    // move — the camera stays put and the release lands as a tap.
    if (!activated && !isPanActive(g)) {
      return { gesture: { ...g, lastX: p.x, lastY: p.y }, move: null }
    }
    const dx = activated ? p.x - g.startX : p.x - g.lastX
    const dy = activated ? p.y - g.startY : p.y - g.lastY
    if (dx === 0 && dy === 0) return { gesture: { ...g, lastX: p.x, lastY: p.y }, move: null }
    return {
      gesture: { ...g, lastX: p.x, lastY: p.y },
      move: { kind: 'pan', dx, dy, activated },
    }
  }
  return { gesture: g, move: null }
}

/**
 * touchend → next gesture + whether the release should suppress the
 * synthesized tap. One finger remaining after a pinch re-anchors the pan
 * at that finger — without this the camera jumps by the released finger's
 * offset.
 */
export const gestureEnd = (g: GestureState, remaining: TouchLike[], rect: RectLike): { gesture: GestureState; suppressTap: boolean } => {
  const suppressTap = g.mode === 'pinch' || isPanActive(g)
  if (remaining.length === 0) return { gesture: { ...IDLE_GESTURE }, suppressTap }
  if (remaining.length === 1) {
    const p = toStagePoint(remaining[0], rect)
    return {
      gesture: { mode: 'pan', lastX: p.x, lastY: p.y, startX: p.x, startY: p.y, pinchDist: 0 },
      suppressTap,
    }
  }
  return { gesture: g, suppressTap }
}

/** Pan is "active" once the finger has crossed the dead zone from its
 *  start point — until then the camera stays put and taps survive. */
export const isPanActive = (g: GestureState): boolean =>
  g.mode === 'pan' && Math.hypot(g.lastX - g.startX, g.lastY - g.startY) > TOUCH_TAP_DEAD_ZONE_PX

const isPastDeadZone = (g: GestureState, x: number, y: number): boolean =>
  Math.hypot(x - g.startX, y - g.startY) > TOUCH_TAP_DEAD_ZONE_PX

/**
 * A gesture frame counts as manual camera input — and so pauses Follow —
 * when it is a pinch (any pinch is deliberate) or an activated pan.
 * Sub-dead-zone pan frames do not pause: taps and jitter leave the
 * follow camera alone.
 */
export const shouldPauseFollow = (next: GestureState, move: PanMove | PinchMove | null): boolean =>
  move != null && (move.kind === 'pinch' || isPanActive(next))

export const isPinchRatioSignificant = (ratio: number): boolean =>
  isFinite(ratio) && ratio > 0 && Math.abs(ratio - 1) > PINCH_RATIO_EPSILON

/** Camera-space pan — identical math to the mouse-drag handler so touch
 *  and pointer can't diverge. flipX mirrors deltas under REVERSED GIS
 *  layout, same as onMouseMove. */
export const panCamera = (cam: MapViewState, dxPx: number, dyPx: number, flipX: 1 | -1): MapViewState => ({
  centerLon: cam.centerLon - (flipX * dxPx) / cam.zoomScale,
  centerLat: cam.centerLat + dyPx / cam.zoomScale,
  zoomScale: cam.zoomScale,
})

/** Cursor/point-anchored zoom — the lon/lat under (sx,sy) stays put.
 *  Single source for wheel, +/- buttons and pinch. */
export const zoomAtPoint = (
  sx: number,
  sy: number,
  factor: number,
  source: MapViewState,
  stageWidth: number,
  stageHeight: number,
  minZoom: number,
  maxZoom: number
): MapViewState => {
  const next = Math.min(Math.max(source.zoomScale * factor, minZoom), maxZoom)
  const dx = sx - stageWidth / 2
  const dy = sy - stageHeight / 2
  return {
    centerLon: source.centerLon + dx / source.zoomScale - dx / next,
    centerLat: source.centerLat - dy / source.zoomScale + dy / next,
    zoomScale: next,
  }
}

export const tapSuppressedUntil = (now: number): number => now + TAP_SUPPRESS_MS

export const isTapSuppressed = (suppressUntil: number, now: number): boolean => now < suppressUntil
